import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { defineMessages, FormattedMessage, intlShape } from 'react-intl'
import { compact, pick } from 'lodash'
import { Button, If, Unless } from 'src/Components'
import { getFullLocation } from 'src/helpers/browser'
import LoadingAnimation from 'src/Components/Loading/LineScale'
import ControlButton from './ControlButton'

import styles from './BombBomb.module.scss'

const ERROR_MESSAGES = defineMessages({
  ValidateSession: {
    id: 'BombBomb.error.apiWontConnectButWeHaveKey',
    defaultMessage:
      'We were unable to connect to your BombBomb account. Please ensure that your BombBomb subscription is active.'
  },
  default: {
    id: 'BombBomb.error.default',
    defaultMessage:
      'There was an issue connecting to your BombBomb account and we are looking into it. Please contact support if this issue persists.'
  }
})

const FORM_FIELDS = {
  email: defineMessages({
    label: {
      id: 'BombBomb.form.emailLabel',
      defaultMessage: 'To email address:'
    },
    labelHint: {
      id: 'BombBomb.form.emailLabelHint',
      defaultMessage: '(comma separated)'
    },
    placeholder: {
      id: 'BombBomb.form.emailPlaceholder',
      defaultMessage: 'Email Address'
    }
  }),
  subject: defineMessages({
    label: {
      id: 'BombBomb.form.subjectLabel',
      defaultMessage: 'Subject:'
    },
    placeholder: {
      id: 'BombBomb.form.subjectPlaceholder',
      defaultMessage: 'Subject'
    }
  }),
  body: defineMessages({
    label: {
      id: 'BombBomb.form.bodyLabel',
      defaultMessage: 'Body:'
    },
    placeholder: {
      id: 'BombBombform.bodyPlaceholder',
      defaultMessage: 'Message content…'
    }
  })
}

// Form states
const INIT = 'init'
const SAVE_FORM = 'saveForm'
const SEND_FORM = 'sendForm'

const stateFromProps = ['toEmail', 'toSubject', 'toBody']

const propTypes = {
  intl: intlShape,
  error: PropTypes.shape({
    methodName: PropTypes.string,
    info: PropTypes.string
  }),
  open: PropTypes.bool.isRequired,
  mount: PropTypes.bool.isRequired,
  bombBomb: PropTypes.object.isRequired
}

class BombBomb extends Component {
  static propTypes = propTypes

  constructor (props) {
    super(props)

    this.state = {
      step: INIT,
      loading: false,
      video: null,
      recorderLoaded: false,
      ...pick(props, stateFromProps)
    }
  }

  componentDidUpdate (prevProps) {
    const { mount } = this.props

    if (!prevProps.mount && mount) {
      this.mountBombBomb()
    }
  }

  updateField = fieldName => e => {
    this.setState({
      [fieldName]: e.target.value
    })
  }

  saveVideo = e => {
    e.preventDefault()
    const { bombBomb, videoTitle } = this.props
    const { video } = this.state
    this.setState({ loading: true })

    bombBomb.saveRecordedVideo(
      videoTitle,
      video.videoId,
      video.filename,
      data => {
        this.setState({ loading: false, step: SEND_FORM })
      }
    )
  }

  sendVideoEmail = e => {
    e.preventDefault()
    const { bombBomb, notify } = this.props
    const { toBody, toSubject, toEmail, video } = this.state
    const body = compact([
      toBody,
      `<a href="${getFullLocation()}">View your Homebot Report &rsaquo;&rsaquo;</a>`
    ]).join('<br /><br />')

    const addresses = toEmail.replace(/([\s;,]+)/g, '; ')

    this.setState({ loading: true })

    bombBomb.videoQuickSend(
      {
        subject: toSubject,
        video_id: video.videoId,
        email_addresses: addresses,
        mobile_message: body
      },
      () => {
        this.setState({ loading: false, step: INIT })
        const emailAddresses = addresses.replace(/([\s;,]+)/g, ' & ')
        notify(`Your video was sent to ${emailAddresses}!`)
      }
    )
  }

  mountBombBomb () {
    const { bombBomb } = this.props

    bombBomb.startVideoRecorder(
      {
        target: '#recorder',
        width: 360,
        recorderLoaded: this.setRecorderLoadedState
      },
      video => {
        this.setState({
          video,
          step: SAVE_FORM
        })
      }
    )
  }

  setRecorderLoadedState = () => {
    this.setState({ recorderLoaded: true })
  }

  getErrorMessage () {
    const { error, intl } = this.props
    const message = ERROR_MESSAGES[error.methodName] || ERROR_MESSAGES.default

    return intl.formatMessage(message)
  }

  renderError () {
    return (
      <div className={styles.errorContainer}>
        {this.getErrorMessage()}
        <div className={styles.inlineControl}>
          <ControlButton className={styles.controlButton} />
        </div>
      </div>
    )
  }

  renderSendForm () {
    const { intl } = this.props
    const { toEmail, toSubject, toBody } = this.state

    return (
      <form className={styles.form} onSubmit={this.sendVideoEmail}>
        <h3 className={styles.sendHeadline}>
          <FormattedMessage
            id='BombBomb.form.sendHeadline'
            defaultMessage='Send your video'
          />
        </h3>
        <div className='form-group'>
          <label htmlFor='recorder-emails'>
            {intl.formatMessage(FORM_FIELDS.email.label)}{' '}
            <span className='text-quiet'>
              {intl.formatMessage(FORM_FIELDS.email.labelHint)}
            </span>
          </label>
          <input
            required
            className='placeholder-label'
            type='text'
            value={toEmail}
            onChange={this.updateField('toEmail')}
            placeholder={intl.formatMessage(FORM_FIELDS.email.placeholder)}
            name='recorder-emails'
            autoComplete='off'
          />
        </div>
        <div className='form-group'>
          <label htmlFor='recorder-email-subject'>
            {intl.formatMessage(FORM_FIELDS.subject.label)}
          </label>
          <input
            className='placeholder-label'
            type='text'
            value={toSubject}
            onChange={this.updateField('toSubject')}
            placeholder={intl.formatMessage(FORM_FIELDS.subject.placeholder)}
            name='recorder-email-subject'
            required
          />
        </div>
        <div className='form-group'>
          <label htmlFor='recorder-email-body-content'>
            {intl.formatMessage(FORM_FIELDS.body.label)}
          </label>
          <textarea
            className={styles.textarea}
            value={toBody}
            onChange={this.updateField('toBody')}
            placeholder={intl.formatMessage(FORM_FIELDS.body.placeholder)}
            name='recorder-email-body-content'
            autosize='true'
          />
        </div>
        <div className='button-group'>
          <Button primary type='submit' loading={this.state.loading}>
            <FormattedMessage
              id='BombBomb.recorder.sendButton'
              defaultMessage='Send Email'
            />
          </Button>
        </div>
        <div>
          <Button minimal onClick={() => this.setState({ step: INIT })}>
            <FormattedMessage
              id='BombBomb.recorder.justSaveButton'
              defaultMessage="Just save (don't send)"
            />
          </Button>
        </div>
      </form>
    )
  }

  renderContent () {
    const { step, recorderLoaded } = this.state

    if (this.props.error) {
      return this.renderError()
    }

    return (
      <Fragment>
        <div className={styles.preview}>
          <If condition={!recorderLoaded}>
            <LoadingAnimation className={styles.loader} />
          </If>
          <div id='recorder' />
        </div>
        <div className={styles.inlineControl}>
          <ControlButton className={styles.controlButton} />
        </div>

        <If condition={step === SAVE_FORM}>
          <form className={styles.form} onSubmit={this.saveVideo}>
            <Button primary type='submit' loading={this.state.loading}>
              <FormattedMessage
                id='BombBomb.recorder.saveButton'
                defaultMessage='Save Video'
              />
            </Button>
            <p className={styles.formHelpText}>
              <FormattedMessage
                id='BombBomb.recorder.rerecordMessage'
                defaultMessage='Or click "re-record" on the video'
              />
            </p>
          </form>
        </If>

        <If condition={step === SEND_FORM}>{this.renderSendForm()}</If>
      </Fragment>
    )
  }

  render () {
    const { open } = this.props

    const container = classnames(styles.container, {
      [styles.open]: open
    })

    return (
      <Fragment>
        <Unless condition={open}>
          <div className={styles.control}>
            <ControlButton className={styles.controlButton} />
          </div>
        </Unless>
        <div className={container}>{this.renderContent()}</div>
      </Fragment>
    )
  }
}

export default BombBomb
